// File: frontend/src/components/auth/AuthModal.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import { getCurrentUserContext } from '../../utils/bootstrap';
import styles from './AuthModal.module.css';

// Input Components
import PhoneInput from '../common/PhoneInput';
import EmailInput from '../common/EmailInput';
import TextInput from '../common/TextInput';
import OTPInput from './OTPInput';

const MODAL_TYPES = {
  SIGN_IN: 'sign_in',
  SIGN_UP: 'sign_up'
};

const AuthModal = ({ isOpen, onClose, initialType = MODAL_TYPES.SIGN_IN }) => {
  const { t } = useLanguage();
  const { login } = useAuth();
  
  // Modal state
  const [modalType, setModalType] = useState(initialType);
  const [step, setStep] = useState(1);
  
  // Form data
  const [formData, setFormData] = useState({
    email_address: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    full_phone: '',
    country_code: '+1',
    area_code: '',
    phone_local: ''
  });
  
  // OTP state
  const [otp, setOtp] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [otpError, setOtpError] = useState('');
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Sign-in method (email or phone)
  const [signInMethod, setSignInMethod] = useState('email');
  
  // UI state
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [prepopulatedData, setPrepopulatedData] = useState(null);
  const [userId, setUserId] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Reset modal when it opens/closes
  useEffect(() => {
    if (isOpen) {
      setModalType(initialType);
      setStep(1);
      setOtp('');
      setOtpError('');
      setErrors({});
      setShowSuccess(false);
      setVerificationId('');
      setCorrelationId('');
      setUserId('');
      setRemainingTime(0);
      setRememberMe(false);
      setPrepopulatedData(null);
      setAgreedToTerms(false);
    }
  }, [isOpen, initialType]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      if (window.__lenis) {
        window.__lenis.stop();
      }
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        if (window.__lenis) {
          window.__lenis.start();
        }
        
      };
    }
  }, [isOpen]);
  
  // Track modal opens for analytics
  useEffect(() => {
    if (isOpen) {
      const userContext = getCurrentUserContext();
      if (userContext) {
        const eventType = modalType === MODAL_TYPES.SIGN_IN 
          ? 'USER_SIGN_IN_MODAL_OPENED' 
          : 'USER_SIGN_UP_MODAL_OPENED';
        
        apiService.trackUserAction({
          user_id: userContext.anonymousUserId,
          enterprise_id: userContext.enterpriseId,
          session_id: userContext.sessionId,
          event_type: eventType,
          action: 'modal_opened',
          page: window.location.pathname
        }).catch(error => {
          console.warn('Failed to track modal open:', error);
        });
      }
    }
  }, [isOpen, modalType]);
  
  // Countdown timer
  useEffect(() => {
    let interval;
    if (remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [remainingTime]);
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLinkClick = () => {
    onClose();
  };
  
  // Handle input changes
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const handlePhoneChange = (phoneData) => {    
    setFormData(prev => ({
      ...prev,
      phone_number: phoneData.phoneNumber,
      full_phone: phoneData.fullNumber,
      country_code: phoneData.countryCode || '+1',
      area_code: phoneData.areaCode || '',
      phone_local: phoneData.localNumber || ''
    }));
    
    if (errors.phone_number) {
      setErrors(prev => ({ ...prev, phone_number: null }));
    }
  };
  
  // Switch between sign-in and sign-up
  const switchModalType = (type) => {
    setModalType(type);
    setStep(1);
    setOtp('');
    setErrors({});
    setShowSuccess(false);
    setRememberMe(false);
    setCorrelationId('');
    setUserId('');
    setAgreedToTerms(false);
    
    if (type === MODAL_TYPES.SIGN_IN) {
      setFormData({
        email_address: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        full_phone: '',
        country_code: '+1',
        area_code: '',
        phone_local: ''
      });
      setPrepopulatedData(null);
      
    } else if (type === MODAL_TYPES.SIGN_UP) {
      if (prepopulatedData) {
        setFormData(prev => ({
          ...prev,
          email_address: prepopulatedData.email_address || '',
          phone_number: prepopulatedData.phone_number || '',
          full_phone: prepopulatedData.full_phone || '',
          country_code: prepopulatedData.country_code || '+1',
          area_code: prepopulatedData.area_code || '',
          phone_local: prepopulatedData.phone_local || ''
        }));
        setPrepopulatedData(null);
      } else {
        setFormData({
          email_address: '',
          first_name: '',
          last_name: '',
          phone_number: '',
          full_phone: '',
          country_code: '+1',
          area_code: '',
          phone_local: ''
        });
      }
    }
  };
  
  // Format phone number for display
  const formatPhoneDisplay = (phoneNumber) => {
    if (!phoneNumber) return '';
    // NANP +1 format
    if (phoneNumber.startsWith('+1') && phoneNumber.length === 12) {
      const cleaned = phoneNumber.slice(2);
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    // International: just return as-is, already in E.164
    return phoneNumber;
  };
  
  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Validate if sign-up form is complete
  const isSignUpFormComplete = () => {
    const isNANP = formData.country_code === '+1';
    const phoneValid = isNANP
      ? formData.full_phone.length >= 12 && formData.area_code.length === 3 && formData.phone_local.length === 7
      : formData.full_phone.length >= 10; // country_code + at least 7 digits
  
    return (
      formData.first_name.trim() !== '' &&
      formData.last_name.trim() !== '' &&
      formData.email_address.trim() !== '' &&
      phoneValid &&
      agreedToTerms === true
    );
  };

  // Validate if sign-in form is complete
  const isSignInFormComplete = () => {
    if (signInMethod === 'email') {
      return formData.email_address.trim() !== '';
    } else if (signInMethod === 'phone') {
      const isNANP = formData.country_code === '+1';
      return isNANP
        ? formData.full_phone.length >= 12 && formData.area_code.length === 3 && formData.phone_local.length === 7
        : formData.full_phone.length >= 10;
    }
    return false;
  };
  
  // ============================================
  // SIGN-IN FLOW
  // ============================================
  
  // Validate sign-in form
  const validateSignInForm = () => {
    const newErrors = {};
    
    if (signInMethod === 'email') {
      if (!formData.email_address.trim()) {
        newErrors.email_address = t('auth.errors.email_required', 'Email is required');
      } else if (!/\S+@\S+\.\S+/.test(formData.email_address)) {
        newErrors.email_address = t('auth.errors.email_invalid', 'Please enter a valid email');
      }
    } else if (signInMethod === 'phone') {
      const isNANP = formData.country_code === '+1';
      const minLength = isNANP ? 12 : 10;
      if (!formData.full_phone || formData.full_phone.length < minLength) {
        newErrors.phone_number = t('auth.errors.phone_invalid', 'Please enter a valid phone number');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // SIGN-IN STEP 1: Request OTP
  const handleSignIn = async (e) => {
    e.preventDefault();
    
    if (!validateSignInForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const userContext = getCurrentUserContext();
      
      if (!userContext) {
        throw new Error(t('auth.errors.session_not_initialized', 'Session not initialized. Please refresh the page.'));
      }
      
      const signInData = {
        sign_in_method: signInMethod,
        language: navigator.language.toLowerCase() || 'en-us',
        anonymous_user_id: userContext.anonymousUserId,
        ip_address: null,
        user_agent: navigator.userAgent
      };
      
      if (signInMethod === 'email') {
        signInData.email_address = formData.email_address.toLowerCase().trim();
      } else {
        signInData.phone_number = formData.full_phone;
      }
      
      const response = await apiService.signIn(signInData);
      
      if (response.success) {
        setVerificationId(response.data.verification_id);
        
        if (response.data.correlation_id) {
          setCorrelationId(response.data.correlation_id);
        }
        
        setStep(2);
        setRemainingTime(response.data.expires_in || 120);
      } else {
        if (response.code === 'USER_NOT_FOUND') {
          const prepopData = {
            sign_in_method: response.details.sign_in_method
          };
          
          if (response.details.sign_in_method === 'email') {
            prepopData.email_address = response.details.identifier;
          } else {
            prepopData.phone_number = response.details.identifier;
            const phoneMatch = response.details.identifier.match(/^(\+\d{1,3})(\d{3})(\d+)$/);
            if (phoneMatch) {
              const isNANP = phoneMatch[1] === '+1';
              prepopData.country_code = phoneMatch[1];
              prepopData.area_code = isNANP ? phoneMatch[2] : '';
              prepopData.phone_local = isNANP ? phoneMatch[3] : phoneMatch[2] + phoneMatch[3];
              prepopData.full_phone = response.details.identifier;
            }
          }
          
          setPrepopulatedData(prepopData);
          setErrors({ form: t('auth.messages.welcome_create_account', '👋 Welcome! Let\'s create your account...') });
          
          setTimeout(() => {
            switchModalType(MODAL_TYPES.SIGN_UP);
          }, 1500);
          
        } else if (response.code === 'ACCOUNT_NOT_ACTIVE') {
          const prepopData = {
            sign_in_method: signInMethod
          };
          
          if (signInMethod === 'email') {
            prepopData.email_address = formData.email_address;
          } else {
            prepopData.phone_number = formData.full_phone;
            prepopData.full_phone = formData.full_phone;
            const phoneMatch = formData.full_phone.match(/^(\+\d{1,3})(\d{3})(\d+)$/);

            if (phoneMatch) {
              const isNANP = phoneMatch[1] === '+1';
              prepopData.country_code = phoneMatch[1];
              prepopData.area_code = isNANP ? phoneMatch[2] : '';
              prepopData.phone_local = isNANP ? phoneMatch[3] : phoneMatch[2] + phoneMatch[3];
              // full_phone already set above as formData.full_phone, no overwrite
            }
          }
          
          setPrepopulatedData(prepopData);
          setErrors({ form: t('auth.messages.complete_signup', 'Please complete your sign up...') });
          
          setTimeout(() => {
            switchModalType(MODAL_TYPES.SIGN_UP);
          }, 1000);
          
        } else if (response.code === 'NOT_ENTERPRISE_MEMBER') {
          if (response.details.user_verified) {
            const prepopData = {
              sign_in_method: signInMethod
            };
            
            if (signInMethod === 'email') {
              prepopData.email_address = formData.email_address;
            } else {
              prepopData.phone_number = formData.full_phone;
              prepopData.full_phone = formData.full_phone;
              const phoneMatch = formData.full_phone.match(/^(\+\d{1,3})(\d{3})(\d+)$/);
              if (phoneMatch) {
                const isNANP = phoneMatch[1] === '+1';
                prepopData.country_code = phoneMatch[1];
                prepopData.area_code = isNANP ? phoneMatch[2] : '';
                prepopData.phone_local = isNANP ? phoneMatch[3] : phoneMatch[2] + phoneMatch[3];
                // full_phone stays as formData.full_phone, no overwrite
              }
            }
            
            setPrepopulatedData(prepopData);
            setErrors({ form: t('auth.messages.account_found_join_org', '👋 Account found! Complete sign up to join this organization...') });
            
            setTimeout(() => {
              switchModalType(MODAL_TYPES.SIGN_UP);
            }, 1500);
            
          } else {
            if (response.details.verification_stage === 'phone') {
              setErrors({
                form: t('auth.messages.verify_phone_first', 'Account found! Please verify your phone first.')
              });
              if (response.details.user_id) {
                setUserId(response.details.user_id);
              }
            } else if (response.details.verification_stage === 'email') {
              setErrors({
                form: t('auth.messages.verify_email_first', 'Account found! Please verify your email first.')
              });
              if (response.details.user_id) {
                setUserId(response.details.user_id);
              }
            }
          }
        } else {
          throw new Error(response.error || t('auth.errors.signin_failed', 'Sign-in failed'));
        }
      }
      
    } catch (error) {
      console.error('❌ Sign-in error:', error);
      setErrors({
        form: error.message || t('auth.errors.generic', 'An error occurred. Please try again.')
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // SIGN-IN STEP 2: Verify OTP
  const handleVerifySignInOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 7) {
      setOtpError(t('auth.errors.otp_invalid', 'Please enter a valid 7-digit code'));
      return;
    }
    
    setIsLoading(true);
    setOtpError('');
    
    try {
      const response = await apiService.verifySignInOTP({
        verification_id: verificationId,
        otp_code: otp,
        correlation_id: correlationId
      });
      
      if (response.success) {
        await login(response.data);
        onClose();
      } else {
        throw new Error(response.error || t('auth.errors.verification_failed', 'Verification failed'));
      }
      
    } catch (error) {
      console.error('❌ Sign-in OTP verification error:', error);
      setOtpError(error.message || t('auth.errors.verification_failed_retry', 'Verification failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // ============================================
  // SIGN-UP FLOW
  // ============================================
  
  // Validate sign-up form
  const validateSignUpForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = t('auth.errors.first_name_required', 'First name is required');
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = t('auth.errors.last_name_required', 'Last name is required');
    }
    
    if (!formData.email_address.trim()) {
      newErrors.email_address = t('auth.errors.email_required', 'Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email_address)) {
      newErrors.email_address = t('auth.errors.email_invalid', 'Please enter a valid email');
    }
    
    const isNANP = formData.country_code === '+1';
    if (!formData.full_phone || formData.full_phone.length < 10) {
      newErrors.phone_number = t('auth.errors.phone_invalid', 'Please enter a valid phone number');
    } else if (isNANP && (!formData.area_code || formData.area_code.length !== 3)) {
      newErrors.phone_number = t('auth.errors.phone_area_code_required', 'Please enter a complete phone number with area code');
    } else if (isNANP && (!formData.phone_local || formData.phone_local.length !== 7)) {
      newErrors.phone_number = t('auth.errors.phone_complete_required', 'Please enter a complete phone number');
    }
    
    if (!agreedToTerms) {
      newErrors.terms = t('auth.errors.terms_required', 'You must agree to the Terms of Service and Privacy Policy');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // SIGN-UP STEP 1: Submit form
  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!validateSignUpForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const userContext = getCurrentUserContext();
      
      if (!userContext) {
        throw new Error(t('auth.errors.session_not_initialized', 'Session not initialized. Please refresh the page.'));
      }
      
      const signUpData = {
        anonymous_user_id: userContext.anonymousUserId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email_address: formData.email_address.toLowerCase().trim(),
        phone_number: formData.full_phone,
        country_code: formData.country_code,
        area_code: formData.area_code,
        phone_local: formData.phone_local,
        language: navigator.language.toLowerCase() || 'en-us'
      };
      
      const response = await apiService.signUp(signUpData);
      
      if (response.success) {
        if (response.data.enterprise_joined && response.data.can_sign_in) {
          setShowSuccess(true);
          setErrors({
            form: t('auth.messages.joined_org_redirecting', '✅ You\'ve joined this organization! Redirecting to sign in...')
          });
          
          setTimeout(() => {
            switchModalType(MODAL_TYPES.SIGN_IN);
          }, 2000);
          
        } else {
          setVerificationId(response.data.verification_id);
          
          if (response.data.correlation_id) {
            setCorrelationId(response.data.correlation_id);
          }
          
          if (response.data.user_id) {
            setUserId(response.data.user_id);
          }
          
          setStep(2);
          setRemainingTime(response.data.expires_in || 120);
        }
      } else {
        if (response.code === 'ALREADY_VERIFIED') {
          setErrors({
            form: t('auth.messages.already_verified_signin', 'Your account is already verified. Please sign in instead.')
          });
          setTimeout(() => switchModalType(MODAL_TYPES.SIGN_IN), 2000);
          
        } else if (response.code === 'PHONE_VERIFIED_EMAIL_PENDING') {
          setErrors({
            form: t('auth.messages.phone_verified_check_email', 'Your phone is already verified. Please check {{email}} to complete verification.', { email: response.details.email_address })
          });
          if (response.details.user_id) {
            setUserId(response.details.user_id);
          }
          setStep(3);
          
        } else if (response.code === 'EMAIL_EXISTS') {
          setErrors({
            email_address: response.details.email_address
          });
          
        } else if (response.code === 'PHONE_EXISTS') {
          setErrors({
            phone_number: response.details.phone_number
          });
          
        } else if (response.code === 'EMAIL_VERIFICATION_REQUIRED') {
          setErrors({
            form: t('auth.messages.verify_email_check_inbox', 'Please verify your email first. Check {{email}} for the verification link.', { email: response.details.email_address })
          });
          if (response.details.user_id) {
            setUserId(response.details.user_id);
          }
          
        } else if (response.code === 'PHONE_VERIFICATION_REQUIRED') {
          setErrors({
            form: t('auth.messages.verify_phone_number_first', 'Please verify your phone number first.')
          });
          if (response.details.user_id) {
            setUserId(response.details.user_id);
          }
          
        } else if (response.code === 'ALREADY_MEMBER') {
          setErrors({
            form: t('auth.messages.already_member_signin', 'You\'re already a member of this organization. Please sign in instead.')
          });
          setTimeout(() => switchModalType(MODAL_TYPES.SIGN_IN), 2000);
          
        } else {
          throw new Error(response.error || t('auth.errors.signup_failed', 'Sign-up failed'));
        }
      }
      
    } catch (error) {
      console.error('❌ Sign-up error:', error);
      setErrors({
        form: error.message || t('auth.errors.generic', 'An error occurred. Please try again.')
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // SIGN-UP STEP 2: Verify phone OTP
  const handleVerifyPhoneOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 7) {
      setOtpError(t('auth.errors.otp_invalid', 'Please enter a valid 7-digit code'));
      return;
    }
    
    setIsLoading(true);
    setOtpError('');
    
    try {
      const response = await apiService.verifyPhoneOTP({
        verification_id: verificationId,
        otp_code: otp,
        correlation_id: correlationId
      });
      
      if (response.success) {
        setShowSuccess(true);
        setStep(3);
      } else {
        throw new Error(response.error || t('auth.errors.verification_failed', 'Verification failed'));
      }
      
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      setOtpError(error.message || t('auth.errors.verification_failed_retry', 'Verification failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Resend OTP
  const resendOTP = async () => {
    if (remainingTime > 0) return;
    
    setIsLoading(true);
    setOtpError('');
    
    try {
      const response = await apiService.resendOTP(verificationId, correlationId);
      
      if (response.success) {
        setRemainingTime(response.data.expires_in || 120);
        setOtp('');
      } else {
        throw new Error(response.error || t('auth.errors.resend_code_failed', 'Failed to resend code'));
      }
      
    } catch (error) {
      console.error('❌ Resend OTP error:', error);
      setOtpError(error.message || t('auth.errors.resend_code_failed_retry', 'Failed to resend code. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Resend email verification
  const resendEmail = async () => {
    if (!userId) {
      setErrors({ form: t('auth.errors.resend_email_no_user', 'Unable to resend email. Please try signing up again.') });
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await apiService.resendVerificationEmail(userId, correlationId);
      
      if (response.success) {
        setErrors({
          form: t('auth.messages.verification_email_resent', '✅ Verification email resent! Please check your inbox.')
        });
      } else {
        throw new Error(response.error || t('auth.errors.resend_email_failed', 'Failed to resend email'));
      }
      
    } catch (error) {
      console.error('❌ Resend email error:', error);
      setErrors({
        form: error.message || t('auth.errors.resend_verification_email_failed', 'Failed to resend verification email')
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // ============================================
  // RENDER FUNCTIONS - SIGN-IN
  // ============================================
  
  const renderSignInStep1 = () => (
    <div className={styles.formContent} data-lenis-prevent>
        <div className={styles.formHeader}>
        <h2>{t('auth.signin.title', 'Welcome Back')}</h2>
        <p>{t('auth.signin.subtitle', 'Sign in to access your insurance wallet')}</p>
        </div>
        
        <form onSubmit={handleSignIn} className={styles.formContainer}>
        <div className={styles.methodToggle} data-active={signInMethod}>
            <button 
            type="button"
            className={`${styles.methodButton} ${signInMethod === 'email' ? styles.active : ''}`}
            onClick={() => setSignInMethod('email')}
            >
            {t('auth.signin.method_email', 'Email')}
            </button>
            <button 
            type="button"
            className={`${styles.methodButton} ${signInMethod === 'phone' ? styles.active : ''}`}
            onClick={() => setSignInMethod('phone')}
            >
            {t('auth.signin.method_phone', 'Phone')}
            </button>
        </div>
        
        <div className={styles.signInFormContainer}>
          {signInMethod === 'email' ? (
            <EmailInput
              value={formData.email_address}
              onChange={(value) => handleInputChange('email_address', value)}
              label={t('auth.fields.email_address', 'Email Address')}
              placeholder={t('auth.fields.email_placeholder', 'you@example.com')}
              error={errors.email_address}
              required
            />
          ) : (
            <>
              <PhoneInput
                value={{
                  phoneNumber: formData.phone_number,
                  fullNumber: formData.full_phone
                }}
                onChange={handlePhoneChange}
                label={t('auth.fields.phone_number', 'Phone Number')}
                placeholder={t('auth.fields.phone_placeholder', '(555) 123-4567')}
                error={errors.phone_number}
                required
              />
              <p className={styles.smsDisclaimer}>
                {t('auth.signin.sms_disclaimer_prefix', 'By continuing, you consent to receive a one-time verification code via SMS from Allegory Insurance. Message and data rates may apply. One message per sign in attempt. Reply STOP to opt out or HELP for support, or email support@allegoryinsurance.com. View our')}{' '}
                <Link to="/sms-terms" onClick={handleLinkClick} rel="noopener noreferrer">
                  {t('auth.signin.sms_terms_link', 'SMS Terms')}
                </Link>
                {'.'}
              </p>
            </>
          )}

          <div className={styles.rememberMeContainer}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="rememberMe">{t('auth.signin.remember_me', 'Remember me')}</label>
          </div>
        </div>
        
        {errors.form && <div className={styles.formError}>{errors.form}</div>}
        
        <div className={styles.buttonGroup}>
            <button 
            type="submit" 
            className={styles.primaryButton}
            disabled={isLoading || !isSignInFormComplete()} // ✅ UPDATED
            >
            {isLoading ? t('auth.signin.sending_code', 'Sending Code...') : t('auth.signin.continue', 'Continue')}
            </button>
            
            {/* ✅ Optional: Show hint when form is incomplete */}
            {!isSignInFormComplete() && !isLoading && (
            <p className={styles.formIncompleteHint}>
                {signInMethod === 'email' 
                ? t('auth.signin.enter_email', 'Please enter your email address')
                : t('auth.signin.enter_phone', 'Please enter your complete phone number')
                }
            </p>
            )}
            
            <div className={styles.divider}>
            <span>{t('auth.signin.new_user_prompt', 'New to Allegory Insurance?')}</span>
            </div>
            
            <button 
            type="button"
            onClick={() => switchModalType(MODAL_TYPES.SIGN_UP)}
            className={styles.secondaryButton}
            >
            {t('auth.signin.create_account', 'Create Account')}
            </button>
        </div>
        </form>
    </div>
    );
  
  const renderSignInStep2 = () => (
    <div className={styles.formContent} data-lenis-prevent>
      <div className={styles.formHeader}>
        <h2>{t('auth.signin.verify_title', 'Verify Your Sign-In')}</h2>
        <p>
          {t('auth.signin.verify_subtitle', 'We sent a 7-digit code to {{identifier}}', { 
            identifier: signInMethod === 'email' 
              ? formData.email_address 
              : formatPhoneDisplay(formData.full_phone)
          })}
        </p>
      </div>
      
      <form onSubmit={handleVerifySignInOTP} className={styles.formContainer}>
        <div className={styles.inputGroup}>
          <label>{t('auth.signin.enter_code_label', 'Enter Verification Code')}</label>
          <OTPInput
            length={7}
            onChange={setOtp}
            value={otp}
            autoFocus
          />
        </div>
        
        {otpError && <div className={styles.formError}>{otpError}</div>}
        
        <div className={styles.buttonGroup}>
          <button 
            type="submit" 
            className={styles.primaryButton}
            disabled={isLoading || otp.length !== 7}
          >
            {isLoading ? t('auth.signin.verifying', 'Verifying...') : t('auth.signin.verify_signin', 'Verify & Sign In')}
          </button>
          
          <div className={styles.resendContainer}>
            <button 
              type="button"
              className={styles.resendButton}
              onClick={resendOTP}
              disabled={remainingTime > 0 || isLoading}
            >
              {t('auth.common.resend_code', 'Resend Code')}
            </button>
            {remainingTime > 0 && (
              <span className={styles.timer}>{formatTime(remainingTime)}</span>
            )}
          </div>
          
          <button 
            type="button"
            className={styles.backButton}
            onClick={() => setStep(1)}
            disabled={isLoading}
          >
            {t('auth.common.back', 'Back')}
          </button>
        </div>
      </form>
    </div>
  );
  
  // ============================================
  // RENDER FUNCTIONS - SIGN-UP
  // ============================================
  
  const renderSignUpStep1 = () => (
    <div className={styles.formContent} data-lenis-prevent>
        <div className={styles.formHeader}>
        <h2>{t('auth.signup.title', 'Create Your Account')}</h2>
        <p>{t('auth.signup.subtitle', 'Start managing your insurance in one place')}</p>
        </div>
        
        <form onSubmit={handleSignUp} className={styles.formContainer}>
        <div className={styles.scrollableFormContainer}>
            <div className={styles.formFields}>
            <TextInput
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                label={t('auth.fields.first_name', 'First Name')}
                placeholder={t('auth.fields.first_name_placeholder', 'John')}
                error={errors.first_name}
                required
            />
            
            <TextInput
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                label={t('auth.fields.last_name', 'Last Name')}
                placeholder={t('auth.fields.last_name_placeholder', 'Doe')}
                error={errors.last_name}
                required
            />
            </div>
            
            <EmailInput
            value={formData.email_address}
            onChange={(value) => handleInputChange('email_address', value)}
            label={t('auth.fields.email_address', 'Email Address')}
            placeholder={t('auth.fields.email_placeholder', 'you@example.com')}
            error={errors.email_address}
            required
            />
            
            <PhoneInput
            value={{
                phoneNumber: formData.phone_number,
                fullNumber: formData.full_phone
            }}
            onChange={handlePhoneChange}
            label={t('auth.fields.mobile_phone', 'Mobile Phone Number')}
            placeholder={t('auth.fields.phone_placeholder', '(555) 123-4567')}
            error={errors.phone_number}
            required
            />
            
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="agreedToTerms"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (errors.terms) {
                    setErrors(prev => ({ ...prev, terms: null }));
                  }
                }}
              />
              <label htmlFor="agreedToTerms">
                {t('auth.signup.terms_consent_prefix', 'By creating an account, I agree to the')}{' '}
                <Link to="/terms-of-service" onClick={handleLinkClick} rel="noopener noreferrer">
                  {t('auth.signup.terms_of_service', 'Terms of Service')}
                </Link>
                {', '}
                <Link to="/privacy-policy" onClick={handleLinkClick} rel="noopener noreferrer">
                  {t('auth.signup.privacy_policy', 'Privacy Policy')}
                </Link>
                {', '}
                {t('auth.signup.terms_and', 'and')}{' '}
                <Link to="/sms-terms" onClick={handleLinkClick} rel="noopener noreferrer">
                  {t('auth.signup.sms_terms', 'SMS Terms')}
                </Link>
                {'. '}
                {t('auth.signup.sms_consent_body', 'I consent to receive one-time passcode (OTP) SMS messages from Allegory Insurance for account verification. One message per sign up attempt. Message and data rates may apply. Text STOP to opt out or HELP for support, or email support@allegoryinsurance.com.')}
              </label>
            </div>
            {errors.terms && <div className={styles.formError}>{errors.terms}</div>}
        </div>
        
        {errors.form && <div className={styles.formError}>{errors.form}</div>}
        
        <div className={styles.buttonGroup}>
            <button 
            type="submit" 
            className={styles.primaryButton}
            disabled={isLoading || !isSignUpFormComplete()} // ✅ UPDATED
            >
            {isLoading ? t('auth.signup.creating_account', 'Creating Account...') : t('auth.signup.create_account', 'Create Account')}
            </button>
            
            <div className={styles.signInLink}>
            {t('auth.signup.already_have_account', 'Already have an account?')}{' '}
            <button type="button" onClick={() => switchModalType(MODAL_TYPES.SIGN_IN)}>
                {t('auth.signup.signin_link', 'Sign In')}
            </button>
            </div>
        </div>
        </form>
    </div>
    );
  
  const renderSignUpStep2 = () => (
    <div className={styles.formContent} data-lenis-prevent>
      <div className={styles.formHeader}>
        <h2>{t('auth.signup.verify_phone_title', 'Verify Your Phone')}</h2>
        <p>
          {t('auth.signup.verify_phone_subtitle', 'We sent a 7-digit code to {{phone}}', { phone: formatPhoneDisplay(formData.full_phone) })}
        </p>
      </div>
      
      <form onSubmit={handleVerifyPhoneOTP} className={styles.formContainer}>
        <div className={styles.inputGroup}>
          <label>{t('auth.signup.enter_code_label', 'Enter Verification Code')}</label>
          <OTPInput
            length={7}
            onChange={setOtp}
            value={otp}
            autoFocus
          />
        </div>
        
        {otpError && <div className={styles.formError}>{otpError}</div>}
        
        <div className={styles.buttonGroup}>
          <button 
            type="submit" 
            className={styles.primaryButton}
            disabled={isLoading || otp.length !== 7}
          >
            {isLoading ? t('auth.signup.verifying', 'Verifying...') : t('auth.signup.verify_phone', 'Verify Phone')}
          </button>
          
          <div className={styles.resendContainer}>
            <button 
              type="button"
              className={styles.resendButton}
              onClick={resendOTP}
              disabled={remainingTime > 0 || isLoading}
            >
              {t('auth.common.resend_code', 'Resend Code')}
            </button>
            {remainingTime > 0 && (
              <span className={styles.timer}>{formatTime(remainingTime)}</span>
            )}
          </div>
          
          <button 
            type="button"
            className={styles.backButton}
            onClick={() => setStep(1)}
            disabled={isLoading}
          >
            {t('auth.common.back', 'Back')}
          </button>
        </div>
      </form>
    </div>
  );
  
  const renderSignUpStep3 = () => (
    <div className={styles.formContent} data-lenis-prevent>
      <div className={styles.successContainer}>
        <div className={styles.successIcon}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 4L12 14.01L9 11.01" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3>{t('auth.signup.phone_verified_title', 'Phone Verified!')}</h3>
        <p>{t('auth.signup.check_email_complete', 'Check your email to complete verification')}</p>
        <p className={styles.emailInfo}>{formData.email_address}</p>
        <p className={styles.checkEmailMessage}>
          {t('auth.signup.email_verification_instructions', 'We sent a verification link to your email. Click the link to activate your account.')}
        </p>
        
        {userId && (
          <button 
            type="button"
            className={styles.secondaryButton}
            onClick={resendEmail}
            disabled={isLoading}
            style={{ marginBottom: '1rem' }}
          >
            {t('auth.common.resend_email', 'Resend Email')}
          </button>
        )}
        
        <button 
          type="button"
          className={styles.primaryButton}
          onClick={onClose}
        >
          {t('auth.common.close', 'Close')}
        </button>
      </div>
    </div>
  );
  
  // ============================================
  // MAIN RENDER
  // ============================================
  
  const renderContent = () => {
    if (modalType === MODAL_TYPES.SIGN_IN) {
      switch (step) {
        case 1:
          return renderSignInStep1();
        case 2:
          return renderSignInStep2();
        default:
          return renderSignInStep1();
      }
    } else {
      switch (step) {
        case 1:
          return renderSignUpStep1();
        case 2:
          return renderSignUpStep2();
        case 3:
          return renderSignUpStep3();
        default:
          return renderSignUpStep1();
      }
    }
  };
  
  if (!isOpen) return null;
  
  return createPortal(
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modalContainer}>
        <button className={styles.closeButton} onClick={onClose} aria-label={t('auth.common.close_aria', 'Close')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {renderContent()}
      </div>
    </div>,
    document.body
  );
};

export { MODAL_TYPES };
export default AuthModal;